import { useEffect } from 'react';
import { init as initFullStory, isInitialized, FullStory } from '@fullstory/browser';
import { getRuntimeConfig } from 'helpers/runtimeConfig';

/**
 * Query parameters that must never reach the recorder.
 *
 * Fullstory captures `window.location.href` with every page event and there is
 * no client-side hook to rewrite it, so the only reliable defence is to not be
 * capturing while these are in the URL. Its default org privacy rules blocked
 * `code` but sent `?email=` through verbatim, so this list cannot be delegated
 * to vendor configuration:
 *
 *   Url  -> https://…/?email=victim.pii%40example.com&code=--blocked--
 *   Base -> https://…/?email=--blocked--&code=--blocked--
 *
 * - `email` — this app deliberately accepts an address to pre-fill the sign-in
 *   form (see `hooks/useRedirect`), which is PII.
 * - `code`, `state` — Cognito redirects back to this origin with an OAuth
 *   authorization code (see `aws-exports`); a short-lived credential.
 * - `id_token`, `access_token`, `token`, `refresh_token` — never used by this
 *   app today, listed so a future flow cannot silently start leaking them.
 */
const SENSITIVE_QUERY_PARAMS = ['email', 'code', 'state', 'id_token', 'access_token', 'token', 'refresh_token'];

/**
 * Matches a sensitive name used as a parameter anywhere in the URL, whether it
 * sits in the query (`?code=`, `&code=`) or in the hash (`#access_token=`, the
 * shape an implicit OAuth callback would take). The leading delimiter keeps
 * `?xemail=` and `&emailish=` from matching.
 */
const SENSITIVE_PARAM_PATTERN = new RegExp(`[?&#](${SENSITIVE_QUERY_PARAMS.join('|')})=`, 'i');

const CLEAN_URL_POLL_INTERVAL_MS = 500;
const MAX_DECODE_PASSES = 5;

/**
 * Decode until stable, so a sensitive parameter nested inside another
 * parameter's value is seen. `?backurl=` carries a whole URL of its own
 * (`amplify/services`), so `?backurl=https%3A%2F%2F…%3Ftoken%3Dsecret` must
 * count as dirty even though the outer parameter name is innocuous.
 *
 * `fullyDecoded` is false when the input could not be reduced to a stable
 * value: `decodeURIComponent` threw on a malformed escape, or the value was
 * still changing when the (necessarily bounded) pass limit ran out. The URL is
 * attacker-controlled, so both cases would otherwise hide a parameter behind an
 * encoding the regex cannot see — `?backurl=%E0%A4%A%26token%3DSECRET` throws,
 * and a five-times-encoded value outlasts the limit. Callers must treat an
 * incompletely decoded URL as sensitive.
 */
const decodeDeep = (url: string) => {
  let current = url;

  for (let pass = 0; pass < MAX_DECODE_PASSES; pass += 1) {
    let next;

    try {
      next = decodeURIComponent(current);
    } catch {
      return { url: current, fullyDecoded: false };
    }

    if (next === current) {
      return { url: current, fullyDecoded: true };
    }

    current = next;
  }

  return { url: current, fullyDecoded: false };
};

/**
 * Inspects a whole href rather than just its query: Fullstory records the
 * complete URL, so the hash and any nested URL have to be covered too.
 * Over-matching is acceptable here — it only costs a recording, whereas
 * under-matching leaks a credential, so anything unreadable counts as dirty.
 */
const isSensitiveUrl = (href: string) => {
  const { url, fullyDecoded } = decodeDeep(href);

  return !fullyDecoded || SENSITIVE_PARAM_PATTERN.test(url);
};

const hasSensitiveParams = () => isSensitiveUrl(window.location.href);

/**
 * Fullstory session-recording integration.
 *
 * Headless and unconditional: unified-auth is the pre-authentication app, so
 * there is no user to identify, no session-level context to tag and no
 * per-user flag to gate on. Every visitor is recorded, and the only condition
 * is that an Org ID is provisioned in the remote runtime config
 * (`REACT_APP_FULLSTORY_ORG_ID`) — without an identifier the SDK has nowhere
 * to send data, so it is never touched.
 *
 * Capture is started manually rather than at init, so that a URL carrying
 * sensitive parameters is never recorded. The SDK is loaded immediately (it is
 * inert until `start`), and capture runs only while the URL is clean — the
 * consumers of those parameters strip them once used: `useRedirect` after
 * pre-filling the email, Amplify after exchanging the OAuth code. If they are
 * never stripped, capture never starts: this fails closed, losing a recording
 * rather than leaking credentials or PII.
 *
 * The URL keeps being watched for the component's lifetime: capture is shut
 * down if a sensitive parameter appears later and restarted once it is gone.
 * That watch is a poll, so it reacts within an interval rather than before the
 * fact — closing the gap entirely would mean wrapping the global `history`
 * methods to pre-empt Fullstory's own wrapper, which is not a trade this app
 * makes for a recorder. Nothing here introduces such a parameter after load:
 * the only same-document rewrite is `useRedirect` removing one.
 */
const Fullstory: React.FC = () => {
  const orgId = getRuntimeConfig().env.REACT_APP_FULLSTORY_ORG_ID;

  useEffect(() => {
    if (!orgId) {
      return;
    }

    if (!isInitialized()) {
      // Wrap init — session recorders are a common ad-blocker target; a blocked
      // or failed SDK load must never break the login flow.
      try {
        initFullStory({ orgId, startCaptureManually: true });
      } catch {
        console.warn('[Fullstory] initialization failed');

        return;
      }
    }

    let isCapturing = false;
    let hasStarted = false;

    const syncCaptureWithUrl = () => {
      if (hasSensitiveParams()) {
        if (isCapturing) {
          FullStory('shutdown');
          isCapturing = false;
        }

        return;
      }

      if (!isCapturing) {
        // `start` only takes effect once, on a recorder that has never
        // captured; resuming after a shutdown needs `restart`.
        FullStory(hasStarted ? 'restart' : 'start');
        hasStarted = true;
        isCapturing = true;
      }
    };

    syncCaptureWithUrl();

    // Polled rather than purely event-driven: the parameters are stripped with
    // `history.replaceState`, which fires nothing. `popstate` and `hashchange`
    // cover back/forward and hash navigation without waiting for the interval.
    const timer = window.setInterval(syncCaptureWithUrl, CLEAN_URL_POLL_INTERVAL_MS);
    window.addEventListener('popstate', syncCaptureWithUrl);
    window.addEventListener('hashchange', syncCaptureWithUrl);

    return () => {
      window.clearInterval(timer);
      window.removeEventListener('popstate', syncCaptureWithUrl);
      window.removeEventListener('hashchange', syncCaptureWithUrl);
    };
  }, [orgId]);

  return null;
};

Fullstory.displayName = 'Fullstory';

export default Fullstory;
